/**
 * Composable for tracking user engagement events
 * Tracks page views, time spent, and user interactions
 */
import { ref, onMounted, onBeforeUnmount, unref, toRaw } from 'vue'
import http from '@/api/http'

// Generate a session ID for this browser session
const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

export function useEngagementTracking(locationId, materializationId = null) {
  const startTime = ref(null)
  const isTracking = ref(false)

  /**
   * Track a generic engagement event
   */
  const trackEvent = async (eventType, eventData = null, timeSpent = null) => {
    try {
      // Safely unwrap reactive values to avoid cyclic structure errors
      const locId = unref(locationId)
      const matId = unref(materializationId)
      
      await http.post('/api/v1/feedback/engagement', {
        location_id: typeof locId === 'object' ? toRaw(locId) : locId,
        materialization_id: typeof matId === 'object' ? toRaw(matId) : matId,
        session_id: sessionId,
        event_type: eventType,
        event_data: eventData ? JSON.parse(JSON.stringify(eventData)) : null,
        time_spent_seconds: timeSpent
      })
    } catch (error) {
      // Fail silently - don't interrupt user experience
      if (error?.message?.includes('cyclic')) {
        console.warn('Engagement tracking skipped due to data structure')
      } else {
        console.error('Failed to track engagement event:', error)
      }
    }
  }

  /**
   * Track page view with time spent
   */
  const trackPageView = async () => {
    const locId = unref(locationId)
    if (!locId) {
      console.warn('Cannot track page view without locationId')
      return
    }

    // Calculate time spent if we have a start time
    let timeSpent = null
    if (startTime.value) {
      timeSpent = Math.round((Date.now() - startTime.value) / 1000) // seconds
    }

    await trackEvent('page_view', { page: 'dashboard' }, timeSpent)

    // Reset start time for next page view
    startTime.value = Date.now()
  }

  /**
   * Track section expand/collapse
   */
  const trackSectionToggle = async (sectionName, expanded) => {
    await trackEvent(
      expanded ? 'section_expand' : 'section_collapse',
      { section: sectionName }
    )
  }

  /**
   * Track export/download clicks
   */
  const trackExportClick = async (exportType) => {
    await trackEvent('export_click', { export_type: exportType })
  }

  /**
   * Track share clicks
   */
  const trackShareClick = async () => {
    await trackEvent('share_click')
  }

  /**
   * Start tracking when component mounts
   */
  onMounted(() => {
    startTime.value = Date.now()
    isTracking.value = true

    // Track initial page view
    trackPageView()
  })

  /**
   * Track final time spent when component unmounts
   */
  onBeforeUnmount(() => {
    if (startTime.value && isTracking.value) {
      const timeSpent = Math.round((Date.now() - startTime.value) / 1000)

      // Send final page view with total time spent
      trackEvent('page_view', { page: 'dashboard', final: true }, timeSpent)
    }
  })

  return {
    sessionId,
    trackEvent,
    trackPageView,
    trackSectionToggle,
    trackExportClick,
    trackShareClick
  }
}
